import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const GROUPS = [
  { label: "Family Stories", videos: [{ id: "P8Ss0Ip0fnI", img: "family-1" }, { id: "mSiVVtzOONg", img: "family-2" }] },
  { label: "Holidays", videos: [{ id: "V8dxhhNbUsU", img: "holidays" }] },
  { label: "Memories", videos: [{ id: "AJcycCAtwAE", img: "memories" }] },
  { label: "Recipes", videos: [{ id: "3_FexJvUNAU", img: "recipes" }] },
  { label: "Heirlooms", videos: [{ id: "3_FexJvUNAU", img: "heirlooms" }] },
  { label: "Memorials", videos: [{ id: "3ZuYS823xiU", img: "memorial" }] },
];

const Tile = ({ v, label, onPlay }) => (
  <div>
  <a href={`https://youtube.com/shorts/${v.id}`} target="_blank" rel="noreferrer" className="mx-auto mb-4 block w-4/5 transition-transform duration-300 hover:-rotate-2 hover:scale-105" data-testid={`example-keepsake-${v.img}`} aria-label={`${label} keepsake QR code`}>
    <img src={`/img/examples/${v.img}.png`} alt={`${label} keepsake with QR code`} className="h-auto w-full drop-shadow-lg" loading="lazy" />
  </a>
  <button type="button" onClick={() => onPlay({ ...v, label })} className="group relative block aspect-[9/16] w-full overflow-hidden rounded-xl border-4 border-white bg-[#2C1E16] shadow-lg transition-transform duration-300 hover:-translate-y-1 hover:rotate-[-1deg]" data-testid={`example-tile-${v.img}`} aria-label={`Play ${label} example`}>
    <img src={`/img/samples/${v.img}.jpg`} alt={`${label} example video`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
    <span className="absolute inset-0 flex items-center justify-center bg-black/10 transition-colors group-hover:bg-black/25">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#A83232]/90 text-white shadow-xl transition-transform group-hover:scale-110"><Play size={26} fill="currentColor" className="ml-1" /></span>
    </span>
  </button>
  </div>
);

const Group = ({ g, onPlay }) => (
  <figure className={g.videos.length > 1 ? "col-span-2" : ""} data-testid={`example-group-${g.label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className={`grid gap-4 ${g.videos.length > 1 ? "grid-cols-2" : ""}`}>
      {g.videos.map((v) => <Tile key={v.img} v={v} label={g.label} onPlay={onPlay} />)}
    </div>
    <figcaption className="mt-4 text-center font-serif text-2xl font-bold text-[#2C1E16]">{g.label}</figcaption>
  </figure>
);

export default function Examples() {
  const [playing, setPlaying] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-8" data-testid="examples-page">
      <img src="/img/banner.jpg" alt="Voices 2 Remember - Your voice. Your stories. Their keepsake." className="mx-auto h-auto w-full max-w-4xl rounded-2xl shadow-lg" data-testid="examples-banner" />
      <p className="mt-10 font-script text-2xl text-[#A83232]">See what families are saving</p>
      <h1 className="mt-1 text-4xl font-bold text-[#4A2A18] sm:text-5xl">Example memories</h1>
      <p className="mt-3 max-w-2xl text-lg text-[#5C4A3E]">Scan a QR code with your phone camera or tap any video to watch. Every keepsake's QR code opens a short video just like these.</p>
      <div className="mt-10 grid grid-cols-2 gap-x-5 gap-y-10 md:grid-cols-4 lg:grid-cols-7">
        {GROUPS.map((g) => <Group key={g.label} g={g} onPlay={setPlaying} />)}
      </div>
      <div className="mt-14 text-center">
        <Link to="/create" className="btn-primary" data-testid="examples-create-button">Create My Keepsake</Link>
      </div>
      <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
        <DialogContent className="w-[min(92vw,380px)] max-w-none border-4 border-white bg-black p-0" data-testid="example-player-dialog">
          <DialogTitle className="sr-only">{playing?.label} example</DialogTitle>
          {playing && (
            <iframe className="aspect-[9/16] w-full rounded-md" src={`https://www.youtube.com/embed/${playing.id}?autoplay=1&rel=0&playsinline=1`}
              title={`${playing.label} example`} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" data-testid="example-player-iframe" />
          )}
          <div className="grid grid-cols-2 rounded-b-md bg-[#FAF6F0] text-lg font-bold">
            <a href={`https://youtube.com/shorts/${playing?.id}`} target="_blank" rel="noreferrer" className="py-3 text-center text-[#2E6930]" data-testid="example-player-youtube-link">Open in YouTube</a>
            <button type="button" onClick={() => setPlaying(null)} className="py-3 text-[#A83232]" data-testid="example-player-close">Close</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
